// The sidebar of a workspace's settings: the settings sidebar every settings
// screen uses (a way back, "Workspace settings" with the workspace's name under
// it, a search), then "Personal" and, for those who may use them, "Admin";
// and the index the name opens.
import { m } from "@trameria/cloud-i18n";
import {
  SettingsIndexPage,
  SettingsSidebar as Sidebar,
  useLeave,
} from "@trameria/ui-core";
import { useWorkspace } from "../context";
import { type ListedSettingsItem, useSettingsItems } from "./items";

/** The items in their groups: "Personal", then "Admin" when there is any. */
function useGroups() {
  const { slug } = useWorkspace();
  const { view, items } = useSettingsItems();
  const entry = (i: ListedSettingsItem) => ({
    id: i.id,
    to: `/${slug}/settings/${i.id}`,
    label: i.label,
    icon: i.icon,
    description: i.description,
  });
  const personal = items.filter((i) => i.group === "personal").map(entry);
  const admin = items.filter((i) => i.group === "admin").map(entry);
  return {
    slug,
    view,
    groups: [
      { label: () => m.ws_settings_personal(), items: personal },
      ...(admin.length > 0
        ? [{ label: () => m.ws_settings_admin(), items: admin }]
        : []),
    ],
  };
}

/** /<slug>/settings: every item with its sentence (the old web's index). */
export function SettingsIndex() {
  const { groups } = useGroups();
  return (
    <SettingsIndexPage
      title={m.ws_settings_title()}
      description={m.ws_settings_index_description()}
      groups={groups}
    />
  );
}

/** `folded`: the same sidebar folded to its icons. */
export function SettingsSidebar({ folded = false }: { folded?: boolean }) {
  const { slug, view, groups } = useGroups();
  const leave = useLeave(`/${slug}/settings`, `/${slug}`);
  return (
    <Sidebar
      label={m.ws_settings_of({ name: view.name })}
      title={m.ws_settings_title()}
      subtitle={view.name}
      indexTo={`/${slug}/settings`}
      onBack={leave}
      groups={groups}
      folded={folded}
    />
  );
}
